import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { CalendarPlus, Loader2, Clock, User, Stethoscope, Armchair } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useT } from "@/i18n/I18nProvider";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const STATUS_STYLE = {
  scheduled: "bg-blue-100 text-blue-700",
  checked_in: "bg-amber-100 text-amber-700",
  completed: "bg-emerald-100 text-emerald-700",
  cancelled: "bg-slate-200 text-slate-500",
};
const SUB_STATUS_STYLE = {
  waiting_for_dentist: { key: "waitingForDentist", cls: "bg-amber-100 text-amber-700" },
  in_treatment: { key: "inTreatment", cls: "bg-blue-100 text-blue-700" },
  visit_completed: { key: "visitCompleted", cls: "bg-emerald-100 text-emerald-700" },
};

function today() {
  return new Date().toISOString().slice(0, 10);
}

function weekBounds(dateStr) {
  const d = new Date(dateStr + "T00:00:00");
  const day = (d.getDay() + 6) % 7; // Monday=0
  const monday = new Date(d);
  monday.setDate(d.getDate() - day);
  const days = [];
  for (let i = 0; i < 7; i++) {
    const x = new Date(monday);
    x.setDate(monday.getDate() + i);
    days.push(x.toISOString().slice(0, 10));
  }
  return days;
}

export default function Appointments() {
  const { can, user } = useAuth();
  const t = useT();
  const [date, setDate] = useState(today());
  const [appts, setAppts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [refs, setRefs] = useState({ patients: [], dentists: [], chairs: [], translators: [], types: [] });
  const [view, setView] = useState("day");
  const [form, setForm] = useState({
    patient_id: "", dentist_id: "", chair_id: "", translator_id: "", visit_number: "1",
    date: today(), start_time: "09:00", end_time: "10:00", type: "Consultation", notes: "",
  });

  const load = useCallback(() => {
    setLoading(true);
    if (view === "week") {
      const days = weekBounds(date);
      api.get(`/appointments?date_from=${days[0]}&date_to=${days[6]}`)
        .then((r) => setAppts(r.data)).finally(() => setLoading(false));
    } else {
      api.get(`/appointments?date=${date}`).then((r) => setAppts(r.data)).finally(() => setLoading(false));
    }
  }, [date, view]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    Promise.all([
      api.get("/patients"), api.get("/meta/dentists"), api.get("/meta/chairs"), api.get("/meta/translators"), api.get("/meta/appointment-types"),
    ]).then(([p, d, c, t, at]) =>
      setRefs({ patients: p.data, dentists: d.data, chairs: c.data, translators: t.data, types: at.data })
    ).catch(() => {});
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.patient_id || !form.dentist_id) return toast.error(t("appointments.selectPatientDentist"));
    setSaving(true);
    try {
      await api.post("/appointments", {
        patient_id: form.patient_id,
        dentist_id: form.dentist_id,
        chair_id: form.chair_id || null,
        translator_id: form.translator_id || null,
        visit_number: Number(form.visit_number) || 1,
        start: `${form.date}T${form.start_time}:00`,
        end: `${form.date}T${form.end_time}:00`,
        type: form.type,
        notes: form.notes,
      });
      toast.success(t("appointments.scheduled"));
      setOpen(false);
      setDate(form.date);
      load();
    } catch (err) {
      const status = err.response?.status;
      toast.error(status === 409
        ? `${t("appointments.conflict")}: ${formatApiError(err.response?.data?.detail)}`
        : formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const setStatus = async (a, status) => {
    try {
      await api.patch(`/appointments/${a.id}`, { status });
      toast.success(`${t("appointments.marked")} ${t(`appointments.status_${status}`, status.replace("_", " "))}`);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const startTreatment = async (a) => {
    try {
      await api.post(`/appointments/${a.id}/start-treatment`);
      toast.success(t("appointments.treatmentStarted"));
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const finishVisit = async (a) => {
    try {
      await api.post(`/appointments/${a.id}/finish-visit`);
      toast.success(t("appointments.visitCompleted"));
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const canStartTreatment = ["treating_dentist", "supervising_dentist", "owner"].includes(user?.role);
  const canFinishVisit = ["treating_dentist", "supervising_dentist", "owner", "operations_manager"].includes(user?.role);

  const time = (iso) => (iso ? iso.slice(11, 16) : "");

  return (
    <div className="p-6 sm:p-8 max-w-[1400px]">
      <PageHeader title={t("appointments.title")} subtitle={t("appointments.subtitle")}>
        <div className="flex rounded-lg border border-slate-200 bg-white p-0.5" data-testid="view-toggle">
          {["day", "week"].map((v) => (
            <button key={v} onClick={() => setView(v)} data-testid={`view-${v}`}
              className={`px-3 py-1.5 text-sm rounded-md transition-colors ${view === v ? "bg-navy text-white" : "text-slate-500 hover:text-navy"}`}>
              {t(`appointments.${v}`)}
            </button>
          ))}
        </div>
        <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="w-auto" data-testid="appointment-date-filter" />
        {can("appointments", "create") && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="new-appointment-button" className="bg-navy hover:bg-navy-soft text-white gap-2">
                <CalendarPlus className="h-4 w-4" /> {t("appointments.new")}
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-lg">
              <DialogHeader>
                <DialogTitle className="font-heading">{t("appointments.schedule")}</DialogTitle>
                <DialogDescription>{t("appointments.scheduleDesc")}</DialogDescription>
              </DialogHeader>
              <form onSubmit={submit} className="grid grid-cols-2 gap-4 mt-2" data-testid="appointment-form">
                <div className="space-y-1.5 col-span-2">
                  <Label>{t("appointments.patient")} *</Label>
                  <Select value={form.patient_id} onValueChange={(v) => setForm({ ...form, patient_id: v })}>
                    <SelectTrigger data-testid="appt-patient"><SelectValue placeholder={t("appointments.selectPatient")} /></SelectTrigger>
                    <SelectContent>
                      {refs.patients.map((p) => (
                        <SelectItem key={p.id} value={p.id}>{p.patient_code} · {p.first_name} {p.last_name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label>{t("appointments.dentist")} *</Label>
                  <Select value={form.dentist_id} onValueChange={(v) => setForm({ ...form, dentist_id: v })}>
                    <SelectTrigger data-testid="appt-dentist"><SelectValue placeholder={t("appointments.dentist")} /></SelectTrigger>
                    <SelectContent>
                      {refs.dentists.map((d) => (<SelectItem key={d.id} value={d.id}>{d.full_name}</SelectItem>))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label>{t("appointments.chair")}</Label>
                  <Select value={form.chair_id} onValueChange={(v) => setForm({ ...form, chair_id: v })}>
                    <SelectTrigger data-testid="appt-chair"><SelectValue placeholder={t("appointments.chair")} /></SelectTrigger>
                    <SelectContent>
                      {refs.chairs.map((c) => (<SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label>{t("common.date")}</Label>
                  <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} />
                </div>
                <div className="space-y-1.5 grid grid-cols-2 gap-2">
                  <div className="space-y-1.5">
                    <Label>{t("appointments.start")}</Label>
                    <Input type="time" value={form.start_time} onChange={(e) => setForm({ ...form, start_time: e.target.value })} data-testid="appt-start" />
                  </div>
                  <div className="space-y-1.5">
                    <Label>{t("appointments.end")}</Label>
                    <Input type="time" value={form.end_time} onChange={(e) => setForm({ ...form, end_time: e.target.value })} data-testid="appt-end" />
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label>{t("appointments.translator")}</Label>
                  <Select value={form.translator_id} onValueChange={(v) => setForm({ ...form, translator_id: v })}>
                    <SelectTrigger data-testid="appt-translator"><SelectValue placeholder={t("common.optional")} /></SelectTrigger>
                    <SelectContent>
                      {refs.translators.map((t2) => (<SelectItem key={t2.id} value={t2.id}>{t2.full_name}</SelectItem>))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label>{t("appointments.visit")}</Label>
                  <Select value={form.visit_number} onValueChange={(v) => setForm({ ...form, visit_number: v })}>
                    <SelectTrigger data-testid="appt-visit"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {["1", "2", "3", "4", "5"].map((n) => (<SelectItem key={n} value={n}>{t("appointments.visit")} {n}</SelectItem>))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label>{t("appointments.type")}</Label>
                  <Select value={form.type} onValueChange={(v) => {
                    const ty = refs.types.find((x) => x.name === v);
                    const upd = { ...form, type: v };
                    if (ty?.default_duration_min && form.start_time) {
                      const [h, m] = form.start_time.split(":").map(Number);
                      const end = new Date(2000, 0, 1, h, m + ty.default_duration_min);
                      upd.end_time = `${String(end.getHours()).padStart(2, "0")}:${String(end.getMinutes()).padStart(2, "0")}`;
                    }
                    setForm(upd);
                  }}>
                    <SelectTrigger data-testid="appt-type"><SelectValue placeholder={t("appointments.type")} /></SelectTrigger>
                    <SelectContent>
                      {refs.types.map((ty) => (<SelectItem key={ty.id} value={ty.name}>{ty.name}</SelectItem>))}
                    </SelectContent>
                  </Select>
                </div>
                <DialogFooter className="col-span-2 mt-2">
                  <Button type="submit" data-testid="appt-submit" disabled={saving} className="bg-navy hover:bg-navy-soft text-white">
                    {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : t("appointments.schedule")}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </PageHeader>

      {loading ? (
        <div className="p-16 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-navy" /></div>
      ) : view === "week" ? (
        <div className="grid grid-cols-1 md:grid-cols-7 gap-3" data-testid="week-grid">
          {weekBounds(date).map((d) => {
            const dayAppts = appts.filter((a) => (a.start || "").slice(0, 10) === d);
            const dt = new Date(d + "T00:00:00");
            return (
              <div key={d} className="bg-white rounded-xl border border-slate-200 min-h-[140px]" data-testid={`week-day-${d}`}>
                <div className={`px-3 py-2 border-b border-slate-100 text-center ${d === today() ? "bg-navy text-white rounded-t-xl" : ""}`}>
                  <p className="text-xs uppercase tracking-wide opacity-70">{dt.toLocaleDateString(undefined, { weekday: "short" })}</p>
                  <p className="font-heading font-semibold tabular-nums">{dt.getDate()}</p>
                </div>
                <div className="p-2 space-y-2">
                  {dayAppts.length === 0 && <p className="text-center text-xs text-slate-300 py-4">—</p>}
                  {dayAppts.map((a) => (
                    <div key={a.id} data-testid={`appointment-${a.id}`} className={`rounded-lg p-2 text-xs border ${a.status === "cancelled" ? "opacity-50" : ""} border-slate-100 bg-slate-50`}>
                      <p className="font-semibold text-navy tabular-nums">{time(a.start)}–{time(a.end)}</p>
                      <p className="text-slate-700 truncate">{a.patient_name}</p>
                      <p className="text-slate-400 truncate">{a.dentist_name}</p>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      ) : appts.length === 0 ? (
        <div className="bg-white rounded-xl border border-slate-200 p-16 text-center text-slate-400">
          {t("appointments.noAppointments")} {date}
        </div>
      ) : (
        <div className="space-y-3">
          {appts.map((a) => (
            <div key={a.id} data-testid={`appointment-${a.id}`} className="bg-white rounded-xl border border-slate-200 p-4 flex items-center gap-4 stat-lift">
              <div className="flex flex-col items-center justify-center w-20 shrink-0 border-r border-slate-100 pr-4">
                <span className="font-heading text-lg font-semibold text-navy tabular-nums">{time(a.start)}</span>
                <span className="text-xs text-slate-400 tabular-nums">{time(a.end)}</span>
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-medium text-slate-900 inline-flex items-center gap-1.5"><User className="h-4 w-4 text-slate-400" />{a.patient_name}</span>
                  <Badge className={`${STATUS_STYLE[a.status]} hover:${STATUS_STYLE[a.status]} font-normal`}>{t(`appointments.status_${a.status}`, a.status.replace("_", " "))}</Badge>
                  {SUB_STATUS_STYLE[a.sub_status] && (
                    <Badge data-testid={`sub-status-${a.id}`} className={`${SUB_STATUS_STYLE[a.sub_status].cls} font-normal`}>{t(`appointments.${SUB_STATUS_STYLE[a.sub_status].key}`)}</Badge>
                  )}
                </div>
                <div className="flex items-center gap-4 mt-1 text-sm text-slate-500 flex-wrap">
                  <span className="inline-flex items-center gap-1.5"><Stethoscope className="h-3.5 w-3.5" />{a.dentist_name}</span>
                  {a.chair_name && <span className="inline-flex items-center gap-1.5"><Armchair className="h-3.5 w-3.5" />{a.chair_name}</span>}
                  <span className="inline-flex items-center gap-1.5"><Clock className="h-3.5 w-3.5" />{a.type}</span>
                  {a.visit_number && <Badge variant="secondary" className="font-normal">{t("appointments.visit")} {a.visit_number}</Badge>}
                </div>
              </div>
              {canStartTreatment && a.sub_status === "waiting_for_dentist" && a.status !== "cancelled" && a.status !== "completed" && (
                <Button size="sm" onClick={() => startTreatment(a)} data-testid={`start-treatment-${a.id}`} className="bg-blue-600 hover:bg-blue-700 text-white shrink-0">{t("appointments.startTreatment")}</Button>
              )}
              {canFinishVisit && a.sub_status === "in_treatment" && (
                <Button size="sm" onClick={() => finishVisit(a)} data-testid={`finish-visit-${a.id}`} className="bg-emerald-600 hover:bg-emerald-700 text-white shrink-0">{t("appointments.finishVisit")}</Button>
              )}
              {can("appointments", "edit") && a.status !== "cancelled" && a.status !== "completed" && (
                <div className="flex gap-2 shrink-0">
                  {a.status === "scheduled" && (
                    <Button size="sm" variant="outline" onClick={() => setStatus(a, "checked_in")} data-testid={`checkin-${a.id}`}>{t("appointments.checkIn")}</Button>
                  )}
                  <Button size="sm" variant="outline" onClick={() => setStatus(a, "completed")} data-testid={`complete-${a.id}`}>{t("appointments.complete")}</Button>
                  <Button size="sm" variant="ghost" className="text-red-500" onClick={() => setStatus(a, "cancelled")}>{t("common.cancel")}</Button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
